import React from "react";
import { CheckIcon } from "lucide-react";
import { CTAButton } from "../CTAButton";
import { SectionHeading } from "../SectionHeading";
import { atlasSteps, images, volumes } from "../../data/content";
type Volume = (typeof volumes)[number];
function VolumeCard({
  v
}: {
  v: Volume;
}) {
  return <article className="flex flex-col rounded-3xl border border-navy-100 bg-white p-6 sm:p-7" data-id="element-326" data-mp="2rt6yacc1a36s">
      <p className="text-sm font-bold uppercase tracking-[0.14em] text-gold-600" data-id="element-327" data-mp="2e21tkvtt6kpl">Volume {v.number}</p>
      <h3 className="mt-2 font-serif text-2xl font-bold leading-tight text-ink" data-id="element-328" data-mp="2itjvbm3nopbm">{v.title}</h3>
      <p className="mt-3 text-base leading-relaxed text-ink/80" data-id="element-329" data-mp="244xgdlhxxzkx">{v.intro}</p>
      {v.items.length > 0 && <ul className="mt-4 space-y-2" data-id="element-330" data-mp="292pamm625xsa">
          {v.items.map(item => <li key={item} className="flex items-start gap-2.5 text-[15px] text-ink" data-id="element-331" data-mp="1v3zcd37i5s2c">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold-600" aria-hidden="true" data-id="element-332" data-mp="1z5b35c244jc4" />
              {item}
            </li>)}
        </ul>}
      <p className="mt-auto pt-4 text-[15px] italic leading-relaxed text-muted" data-id="element-333" data-mp="1fhp8cm5ppyjl">{v.body}</p>
    </article>;
}
export function VolumesSection() {
  const first = volumes.slice(0, 3);
  const last = volumes.slice(3);
  return <section className="w-full bg-cream px-5 py-16 sm:py-24" data-id="element-334" data-mp="1pf3iy54yft3z">
      <div className="mx-auto max-w-5xl" data-id="element-335" data-mp="1kiwekgs7ipo5">
        <SectionHeading eyebrow="What’s inside" title={<>What I put inside the Blepharoplasty <em className="text-gold-600" data-id="element-337" data-mp="wqfuk0owsnm7">Clinical Pathway™</em></>} data-id="element-336" data-mp="wssc4cqr8vyq" />
        <img src={images.mainMockup} alt="All six volumes of the Blepharoplasty Clinical Pathway" className="mx-auto mt-8 w-full max-w-2xl" data-id="element-338" data-mp="rv8xhbq5ah69" />

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3" data-id="element-339" data-mp="11rviq1mdva8t">
          {first.map(v => <VolumeCard key={v.number} v={v} data-id="element-340" data-mp="e2atj0zmzgr3" />)}
        </div>

        <article className="mt-5 rounded-3xl bg-navy-900 p-6 sm:p-10" data-id="element-341" data-mp="e06je1b59lid">
          <span className="inline-block rounded-full bg-gold-500 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-navy-900" data-id="element-342" data-mp="92et1bq5e7ga">The heart of the system</span>
          <p className="mt-4 text-sm font-bold uppercase tracking-[0.14em] text-gold-400" data-id="element-343" data-mp="12sf5l1j6pf21">Volume IV</p>
          <h3 className="mt-2 font-serif text-3xl font-bold leading-tight text-white sm:text-4xl" data-id="element-344" data-mp="17j5eupuqe220">The Blepharoplasty Case Atlas™</h3>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-345" data-mp="1ci8j482w048x">
            The part I expect you’ll return to again and again. <strong className="text-white" data-id="element-346" data-mp="fuu50hkpgje2">20 educational cases</strong> structured so you don’t simply read the case and
            jump to the review. Instead:
          </p>
          <ol className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-id="element-347" data-mp="orun66h6ll92">
            {atlasSteps.map((s, i) => <li key={s} className="flex items-center gap-3 rounded-xl bg-navy-800 px-4 py-3" data-id="element-348" data-mp="tq5xsjfavzsc" data-mpi={i}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-500 font-serif font-bold text-navy-900" data-id="element-349" data-mp="20af82m6a98yb">{i + 1}</span>
                <span className="text-base font-medium text-white" data-id="element-350" data-mp="3poew8xmkh83p">{s}</span>
              </li>)}
          </ol>
          <p className="mt-6 font-serif text-xl font-bold text-gold-400" data-id="element-351" data-mp="3kqvehnqnud70">That’s deliberate case practice.</p>
        </article>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-352" data-mp="3fxagvwszbl8c">
          {last.map(v => <VolumeCard key={v.number} v={v} data-id="element-353" data-mp="3aypo2ig243tx" />)}
        </div>

        <div className="mx-auto mt-10 max-w-xl" data-id="element-354" data-mp="3709vlz17i394">
          <CTAButton label="I want all 6 volumes" data-id="element-355" data-mp="31zcgxwfenrcd" />
        </div>
      </div>
    </section>;
}